// src/SpectatorView.js
// Vista de espectador para administradores y vendedores
// NO crea engagement events, NO permite pujar, NO requiere invitación

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Eye, AlertCircle } from 'lucide-react';
import { getPropertyIcon } from './constants/propertyIcons';
import { getPropertyStatus } from './utils/propertyHelpers';

// Componentes reutilizados de PropertyView
import PropertyHeader from './components/PropertyHeader';
import PropertyContent from './components/PropertyContent';
import NotificationToast from './components/NotificationToast';

// Servicios (sin engagement)
import { supabase } from './supabaseClient';
import { subscribeToProperty, unsubscribe } from './services/supabaseService';

/**
 * Vista de espectador - permite ver la propiedad exactamente como un comprador
 * pero sin contaminar métricas de engagement
 * 
 * URL: /property/:propertyId/spectate/:spectatorToken
 */
const SpectatorView = () => {
  const { propertyId, spectatorToken } = useParams();
  
  // Estados
  const [property, setProperty] = useState(null);
  const [propertyBids, setPropertyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Ref para usar en callbacks de suscripción (igual que usePropertyState)
  const propertyRef = useRef(null);

  // Cargar datos de la propiedad
  useEffect(() => {
    const loadProperty = async () => {
      try {
        setLoading(true);
        setError(null);

        // Verificar token de espectador
        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .select('*')
          .eq('id', propertyId)
          .eq('spectator_token', spectatorToken)
          .single();

        if (propertyError || !propertyData) {
          setError('INVALID_TOKEN');
          return;
        }

        // Verificar si está archivada
        if (!propertyData.is_active) {
          setError('ARCHIVED');
          return;
        }

        // Mapear datos de propiedad
        const mappedProperty = {
          id: propertyData.id,
          title: propertyData.title,
          description: propertyData.description,
          videoUrl: propertyData.video_url,
          floorPlanUrl: propertyData.floor_plan_url,
          photoUrls: propertyData.photo_urls || [],
          documentUrls: propertyData.document_urls || [],
          startPrice: parseFloat(propertyData.start_price),
          minAbsolute: parseFloat(propertyData.min_absolute),
          minIncrement: parseFloat(propertyData.min_increment),
          maxBidPercentage: parseFloat(propertyData.max_bid_percentage),
          biddingStartDate: propertyData.bidding_start_date,
          deadline: propertyData.deadline,
          createdAt: propertyData.created_at,
          isActive: propertyData.is_active,
          adminEmails: propertyData.admin_emails || [],
          last_chance_started_at: propertyData.last_chance_started_at,
          last_chance_duration: propertyData.last_chance_duration || 48, // Deprecated: usar last_chance_deadline
          last_chance_deadline: propertyData.last_chance_deadline,
          last_chance_closed_at: propertyData.last_chance_closed_at,
          original_deadline: propertyData.original_deadline,
          calendlyUrl: propertyData.calendly_url || '',
          propertyMetadata: propertyData.property_metadata || {},
          // Campos para fase de oferta ciega
          blind_bidding_deadline: propertyData.blind_bidding_deadline,
          blind_bidding_started_at: propertyData.blind_bidding_started_at,
          blind_bidding_duration: propertyData.blind_bidding_duration || 72,
          blind_bidding_closed_at: propertyData.blind_bidding_closed_at,
        };

        setProperty(mappedProperty);
        propertyRef.current = mappedProperty;

        // Cargar ofertas
        const { data: bidsData, error: bidsError } = await supabase
          .from('bids')
          .select('*')
          .eq('property_id', propertyId)
          .order('amount', { ascending: false })
          .order('bid_time', { ascending: true });

        if (!bidsError && bidsData) {
          const mappedBids = bidsData.map(bid => ({
            id: bid.id,
            propertyId: bid.property_id,
            buyerName: bid.buyer_name,
            buyerEmail: bid.buyer_email,
            amount: parseFloat(bid.amount),
            timestamp: bid.bid_time,
            conditionsText: bid.conditions_text,
            is_last_chance: bid.is_last_chance || false,
          }));
          setPropertyBids(mappedBids);
        }

      } catch (err) {
        console.error('Error cargando propiedad:', err);
        setError('LOAD_ERROR');
      } finally {
        setLoading(false);
      }
    };

    if (propertyId && spectatorToken) {
      loadProperty();
    }
  }, [propertyId, spectatorToken]);

  // Suscripción en tiempo real a bids (para ver actualizaciones)
  useEffect(() => {
    if (!propertyId) return;

    const subscription = supabase
      .channel(`spectator-bids:${propertyId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids',
          filter: `property_id=eq.${propertyId}`,
        },
        (payload) => {
          const newBid = {
            id: payload.new.id,
            propertyId: payload.new.property_id,
            buyerName: payload.new.buyer_name,
            buyerEmail: payload.new.buyer_email,
            amount: parseFloat(payload.new.amount),
            timestamp: payload.new.bid_time,
            conditionsText: payload.new.conditions_text,
            is_last_chance: payload.new.is_last_chance || false,
          };
          
          setPropertyBids(prev => {
            const updated = [newBid, ...prev.filter(b => b.id !== newBid.id)];
            return updated.sort((a, b) => b.amount - a.amount || new Date(a.timestamp) - new Date(b.timestamp));
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [propertyId]);

  // Suscripción en tiempo real a cambios en la propiedad (fechas, deadlines, estado)
  // Replica el mismo sistema que usePropertyState para mantener el contador actualizado
  useEffect(() => {
    if (!propertyId) return;

    const subscription = subscribeToProperty(propertyId, (updatedData) => {
      const current = propertyRef.current;
      if (!current) return;

      // Mismo patrón que usePropertyState: merge camelCase + snake_case del payload
      const updated = { ...current, ...updatedData };
      setProperty(updated);
      propertyRef.current = updated;
    });

    return () => {
      unsubscribe(subscription);
    };
  }, [propertyId]);

  // Handler para intentar pujar (bloqueado)
  const handleBidAttempt = () => {
    setNotification({
      type: 'warning',
      title: 'Solo visualización',
      message: 'Este enlace es solo para visualización. No puedes realizar ofertas.'
    });
    setTimeout(() => setNotification(null), 5000);
  };

  // ============================================
  // RENDER: Estados de carga y error
  // ============================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  if (error === 'INVALID_TOKEN') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/30 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <AlertCircle className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Enlace no válido</h2>
          <p className="text-slate-400">
            Este enlace de visualización no es válido o ha expirado.
          </p>
        </div>
      </div>
    );
  }

  if (error === 'ARCHIVED') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-amber-500/30 text-center">
          <div className="bg-amber-500/10 p-4 rounded-xl inline-block mb-4">
            <Building2 className="w-12 h-12 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Propiedad no disponible</h2>
          <p className="text-slate-400">
            Esta propiedad ya no está disponible para visualización.
          </p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/30 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <AlertCircle className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Error</h2>
          <p className="text-slate-400">
            No se pudo cargar la propiedad. Intenta de nuevo más tarde.
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Vista de espectador
  // ============================================

  const status = getPropertyStatus(property);
  const iconConfig = getPropertyIcon(status);
  const hasBids = propertyBids.length > 0;
  const highestBid = propertyBids[0];
  const currentPrice = hasBids ? highestBid.amount : property.startPrice;
  const isClosed = status === 'CLOSED';
  const isUpcoming = status === 'UPCOMING';

  // Calcular total de oferentes únicos (igual que PropertyView)
  const totalBidders = new Set(propertyBids.map(b => b.buyerEmail)).size;
  const showBidders = totalBidders >= 3;

  // Crear invite "falso" para componentes que lo necesitan (solo visualización)
  const spectatorInvite = {
    email: 'spectator@realtybid.com',
    name: 'Espectador',
    token: spectatorToken,
    hasAccess: true,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Banner de modo espectador - Equiparado con estilo de PropertyView */}
      <div className="bg-blue-500/10 border-b border-blue-500/30">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-center gap-2 text-blue-400 text-sm font-medium">
            <Eye className="w-4 h-4" />
            <span>Modo espectador</span>
            <span className="text-blue-400/70">- Solo visualización</span>
          </div>
        </div>
      </div>

      {/* Contenedor principal con padding igual a PropertyView */}
      <div className="max-w-7xl mx-auto p-4 md:p-8">

        {/* Header de propiedad - Ahora con totalBidders y showBidders */}
        <PropertyHeader
          property={property}
          propertyStatus={status}
          statusIcon={iconConfig}
          propertyBids={propertyBids}
          propertyInvites={[]}
          totalBidders={totalBidders}
          showBidders={showBidders}
        />

        {/* Contenido principal - Mismo layout que PropertyView */}
        <PropertyContent
          property={property}
          propertyBids={propertyBids}
          hasBids={hasBids}
          currentPrice={currentPrice}
          highestBid={highestBid}
          userLastBid={null}
          isTopBidder={false}
          isClosed={isClosed}
          isUpcoming={isUpcoming}
          invite={spectatorInvite}
          buyerProfile={null}
          refreshBuyerProfile={() => {}}
          // Props del formulario - bloqueados para espectador
          bidAmount=""
          setBidAmount={() => {}}
          validationError={null}
          setValidationError={() => {}}
          handleBidSubmit={handleBidAttempt}
          handleBlindBidSubmit={handleBidAttempt}
          availableConditions={[]}
          selectedConditions={[]}
          setSelectedConditions={() => {}}
          conditionsText=""
          setConditionsText={() => {}}
          // Sin tracking
          trackEvent={() => {}}
          onScheduleVisit={handleBidAttempt}
          onSaveProfile={() => {}}
        />
      </div>

      {/* Notificaciones */}
      {notification && (
        <NotificationToast
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default SpectatorView;